import React, { useState, useEffect, useCallback, useRef } from 'react';
// --------------------------------------------------------------------------------
import { String, StringProps } from '../String';
import styled from 'styled-components';
import { config } from '../../index';
import { ActionIcon } from '../ActionIcon';

// --------------------------------------------------------------------------------
// 📌  reference to repo for more information
// https://github.com/WoobieTheBear/react-date-picker
// --------------------------------------------------------------------------------

export interface DatePickerProps {
  /* DatePicker border radius */
  borderRadius?: string;
  /* DatePicker label */
  label?: StringProps;
  /* DatePicker name */
  name?: string;
  /* DatePicker onChange callback */
  onChange?: (param?: any) => void;
}

interface InnerProps extends DatePickerProps {
  /* DatePicker button state */
  disabled?: boolean;
  isPast?: boolean;
}

interface FormProps {
  visible?: boolean;
  selectedDay?: Date;
  month?: number;
  year?: number;
  monthDetails?: any;
  update?: boolean;
}

const DatePickerContainer = styled.div<DatePickerProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  user-select: none;
`;

const TodayTimestampContainer = styled.div<DatePickerProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 0 ${config.spacingSmall} 0;
  margin: 0 0 ${config.spacingSmall} 0;
  border-bottom: 1px solid ${config.muted};
`;

const NavigationContainer = styled.div<DatePickerProps>`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const PickerWrapper = styled.div<DatePickerProps>`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: fit-content;
  padding: 16px 12px;
  border: 1px solid ${config.silver};
  border-radius: ${config.spacingXLarge};
  box-shadow: ${config.boxShadow};
`;

const StyledInput = styled.div<DatePickerProps>`
  width: 100%;
  padding: ${config.spacingSmall} ${config.fontXMedium};
  border: 1px solid ${config.silver};
  border-radius: ${(props) => props.borderRadius};
  background-color: ${config.white};
  color: ${config.primary};
  font-size: ${config.fontMedium};
  user-select: none;
  cursor: pointer;

  &::after {
    content: '🗓';
    position: absolute;
    right: -30px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

const Container = styled.div<DatePickerProps>`
  display: flex;
  flex-direction: column;
`;

const WeekDayWrapper = styled.div<DatePickerProps>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-gap: 10px;
  text-align: center;
  font-size: ${config.fontSmall};
  color: ${config.grey};
  text-transform: capitalize;
  margin: ${config.spacingMedium} 0;
`;

const DayPickerWrapper = styled.div<DatePickerProps>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-gap: 10px;
  margin-top: 10px;
  color: ${config.grey};
`;

const StyledButton = styled.div<InnerProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border: none;
  cursor: pointer;
  font-size: ${config.fontMedium};
  color: ${config.secondary};
  padding: 8px;
  border-radius: 50%;
  transition: ${config.transitionShort};
  background-color: ${config.silver};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:hover {
    color: ${(props) =>
      props.disabled || props.isPast ? config.secondary : config.white};
    background-color: ${(props) =>
      props.disabled || props.isPast ? config.silver : config.primary};
      opacity: ${(props) => (props.isPast ? 0.5 : 1)}};
  }
`;

export const DatePicker: React.FC<DatePickerProps> = ({
  onChange,
  borderRadius,
}) => {
  const oneDay = 60 * 60 * 24 * 1000;
  const timeZoneOffsetInMinutes = -new Date().getTimezoneOffset();
  const todayTimestamp = Date.now() - (Date.now() % oneDay);
  // 👉 get date today in dd month yyyy format
  const today = new Date(todayTimestamp).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  // 👉 convert selected date in to day month year format
  const datePrettifier = (date: Date) => {
    return date?.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const [form, setForm] = useState<FormProps>({
    visible: false,
    // selectedDay: new Date(todayTimestamp), // 👉 initial value for the date picker
    month: new Date(todayTimestamp).getMonth(),
    year: new Date(todayTimestamp).getFullYear(),
  });
  const inputRef = useRef<any | undefined>(undefined); // TODO fix ref type

  const MONTH_ARRAY = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const getMonthStr = (month: number) =>
    MONTH_ARRAY[Math.max(Math.min(11, month), 0)] || 'Month';

  const equalDate = (leftHand: Date, rightHand: Date) => {
    return (
      leftHand.getFullYear() === rightHand.getFullYear() &&
      leftHand.getMonth() === rightHand.getMonth() &&
      leftHand.getDate() === rightHand.getDate()
    );
  };

  const getMonthDetails = useCallback(
    (year: number, month: number) => {
      // this months data
      const firstDay = new Date(year, month).getDay();
      const numberOfDays = 40 - new Date(year, month, 40).getDate();
      const numberOfFields = Math.ceil((firstDay + numberOfDays) / 7) * 7;

      // next months data
      const { _year: prevYear, _month: prevMonth } = countMonthDown(
        year,
        month
      );
      const numberOfDaysPrevMonth =
        40 - new Date(prevYear, prevMonth, 40).getDate();

      const monthArray = [...Array.from(Array(numberOfFields).keys())].map(
        (index) => {
          const delta = index - firstDay;
          let dayOfMonth = 0;
          let appearance = 'invalid';
          let dateObject = null;

          if (delta < 0) {
            // day is from previous month
            dayOfMonth = numberOfDaysPrevMonth + delta + 1;
            const { _year, _month } = countMonthDown(year, month);
            dateObject = new Date(_year, _month, dayOfMonth);
            dateObject.setMinutes(timeZoneOffsetInMinutes);
          } else if (delta >= numberOfDays) {
            // day is from next month
            dayOfMonth = delta - numberOfDays + 1;
            const { _year, _month } = countMonthUp(year, month);
            dateObject = new Date(_year, _month, dayOfMonth);
            dateObject.setMinutes(timeZoneOffsetInMinutes);
          } else {
            // day is from this month
            dayOfMonth = delta + 1;
            dateObject = new Date(year, month, dayOfMonth);
            dateObject.setMinutes(timeZoneOffsetInMinutes);

            appearance =
              form?.selectedDay && equalDate(form?.selectedDay, dateObject)
                ? 'selected'
                : dateObject.valueOf() === todayTimestamp
                ? 'current'
                : 'valid';
          }
          // NOTE: these dateObjects have timezone offset
          return {
            appearance,
            dateObject,
          };
        }
      );

      return monthArray;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [todayTimestamp, form?.selectedDay]
  );

  const updateDetails = useCallback(() => {
    const details = getMonthDetails(
      form?.year as number,
      form?.month as number
    );
    setForm({ ...form, monthDetails: details });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMonthDetails, form?.month, form?.year]);

  useEffect(() => {
    if (!form?.update) {
      updateDetails();
      return () => {
        setForm({ ...form, update: true });
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.update, updateDetails]);

  const countMonthUp = (year: number, month: number) => {
    let _year = year;
    let _month = month + 1;
    if (_month > 11) {
      _month = 0;
      _year++;
    }
    return { _year, _month };
  };

  const countMonthDown = (year: number, month: number) => {
    let _year = year;
    let _month = month - 1;
    if (_month < 0) {
      _month = 11;
      _year--;
    }
    return { _year, _month };
  };

  const selectDay = ({ dateObject }: { dateObject: Date }) => {
    // ⚠️ don't allow to select days in the past
    if (dateObject.valueOf() < todayTimestamp) {
      return;
    }

    inputRef.current = dateObject;
    setForm({
      ...form,
      selectedDay: dateObject,
      visible: false,
      update: false,
    });

    onChange?.(dateObject); // 👉 call onChange function with selected date
  };

  const selectYear = (year: number, month: number, offset: number) => {
    let updatedYear = year + offset;
    setForm({
      ...form,
      year: updatedYear,
      monthDetails: getMonthDetails(updatedYear, month),
    });
  };

  const selectMonth = (year: number, month: number, offset: number) => {
    const { _year, _month } =
      offset > 0 ? countMonthUp(year, month) : countMonthDown(year, month);

    setForm({
      ...form,

      year: _year,
      month: _month,
      monthDetails: getMonthDetails(_year, _month),
    });
  };

  const showDatePicker = () => {
    setForm({ ...form, visible: !form?.visible });
  };

  const inputOnKey = (event: { code: string; preventDefault: () => void }) => {
    if (event.code === 'Enter') {
      event.preventDefault();
      let dateValue = new Date(inputRef.current);

      if (dateValue.valueOf() > 0) {
        setForm({
          ...form,
          selectedDay: dateValue,
          year: dateValue.getFullYear(),
          month: dateValue.getMonth(),
          visible: false,
          update: false,
        });

        if (onChange) {
          onChange(dateValue.valueOf());
        }
      } else {
        onChange?.(-1);
      }
    }
    if (event.code === 'Escape') {
      setForm({ ...form, visible: false });
    }
  };

  return (
    <div className="container">
      <DatePickerContainer className="DatePicker0">
        <StyledInput borderRadius={borderRadius} onClick={showDatePicker}>
          {datePrettifier(form?.selectedDay as Date) ?? 'Add date'}
        </StyledInput>
        <input
          type="datetime-local"
          ref={inputRef}
          onKeyDown={inputOnKey}
          style={{ display: 'none' }} // 👉 hide the in UI
        />

        {form?.visible && (
          <PickerWrapper>
            <TodayTimestampContainer>
              <String
                string={today}
                align="center"
                fontSize={config.fontXMedium}
                color={config.secondary}
              />
            </TodayTimestampContainer>
            <NavigationContainer>
              <ActionIcon
                icon="chevron-left"
                border={`1px solid ${config.transparent}`}
                fill={config.secondary}
                hoverFill={config.silver}
                onClick={() =>
                  selectYear(form?.year as number, form?.month as number, -1)
                }
                display={false}
              />
              <ActionIcon
                icon="arrow-left"
                border={`1px solid ${config.primary}`}
                fill={config.secondary}
                hoverFill={config.silver}
                size={12}
                onClick={() =>
                  selectMonth(form?.year as number, form?.month as number, -1)
                }
              />{' '}
              <Container>
                <String
                  string={form?.year + ''}
                  align="center"
                  fontSize={config.fontMedium}
                  color={config.secondary}
                />
                <String
                  string={getMonthStr(form?.month as number)}
                  align="center"
                  fontSize={config.fontXMedium}
                  color={config.secondary}
                />
              </Container>
              <ActionIcon
                icon="arrow-right"
                border={`1px solid ${config.primary}`}
                fill={config.secondary}
                hoverFill={config.silver}
                size={12}
                onClick={() =>
                  selectMonth(form?.year as number, form?.month as number, 1)
                }
              />
              <ActionIcon
                icon="chevron-right"
                border={`1px solid ${config.transparent}`}
                fill={config.secondary}
                hoverFill={config.silver}
                onClick={() =>
                  selectYear(form?.year as number, form?.month as number, 1)
                }
                display={false}
              />
            </NavigationContainer>

            <Container>
              <WeekDayWrapper>
                {['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'].map(
                  (day, index) => (
                    <span key={index}>{day}</span>
                  )
                )}
              </WeekDayWrapper>

              <DayPickerWrapper>
                {form?.monthDetails?.map(
                  (
                    entry: { appearance?: any; dateObject: any },
                    key: number
                  ) => {
                    // 👉 check if date object is in the past
                    const isPast = entry.dateObject.valueOf() < todayTimestamp;

                    return (
                      <StyledButton
                        key={key}
                        isPast={isPast}
                        disabled={entry?.appearance === 'invalid'}
                        onMouseDown={() => selectDay(entry)}
                      >
                        {entry.dateObject.getDate()}
                      </StyledButton>
                    );
                  }
                )}
              </DayPickerWrapper>
            </Container>
          </PickerWrapper>
        )}
      </DatePickerContainer>
    </div>
  );
};
